import { useEffect, useState, useMemo } from "react";
import RightModal from "../components/templates/RightModal";
import { getServicesList, selectServicesData, selectServicesLoading } from "../redux/services/servicesSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import AddEditService from "../components/servicesPage/AddEditService";
import DeleteServiceConfirmation from "../components/servicesPage/DeleteServiceConfirmation";
import { useColorPalette } from '../hooks/useColorPalette';
import DataTable from "../components/DataTable/DataTable";

export default function Services() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const servicesList = useSelector(selectServicesData);
    const servicesLoading = useSelector(selectServicesLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [dataTableKey, setDataTableKey] = useState(Date.now());
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalServiceId = useSelector(selectRightModalId);
    
    // Stato per la ricerca nella DataTable
    const [searchTerm, setSearchTerm] = useState("");
    
    // Stato per memorizzare tutti i servizi non filtrati
    const [allServices, setAllServices] = useState([]);
    
    // Gestione della paginazione con DataTable
    const handlePageChange = (pageIndex, pageSize) => {
        console.log('Cambio pagina a:', pageIndex, 'dimensione:', pageSize);
        // Calcolo l'offset in base all'indice della pagina e alla dimensione
        const start = pageIndex * pageSize;
        
        dispatch(getServicesList({
            accountId: accountData?._id,
            token,
            start,
            length: pageSize,
            search: searchTerm.length >= 3 ? searchTerm : '',
            draw: 1,
            order: [],
            columns: [{ data: "name" }, { data: "code" }, { data: "unitTypeLabel" }]
        }));
        dispatch(resetRefreshNow());
    };
    
    // Gestione della ricerca
    const handleSearch = (searchValue) => {
        console.log('Ricerca:', searchValue);
        setSearchTerm(searchValue);
        
        if (searchValue.length >= 3 || searchValue.length === 0) {
            dispatch(getServicesList({
                accountId: accountData?._id,
                token,
                start: 0,
                length: 10,
                search: searchValue.length >= 3 ? searchValue : '',
                draw: 1,
                order: [],
                columns: [{ data: "name" }, { data: "code" }, { data: "unitTypeLabel" }]
            }));
            dispatch(resetRefreshNow());
        }
    };
    
    // Eseguo la chiamata iniziale quando il componente viene montato
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getServicesList({
                accountId: accountData._id,
                token,
                start: 0,
                length: 100, // Aumentiamo il numero per ottenere più servizi in una volta
                draw: 1,
                order: [],
                columns: [{ data: "name" }, { data: "code" }, { data: "unitTypeLabel" }]
            }));
            dispatch(resetRefreshNow());
        }
    }, [dispatch, accountData, token]);
    
    // Aggiorniamo lo stato allServices quando arrivano i dati dal server
    useEffect(() => {
        console.log("servicesList:", servicesList);
        if (servicesList?.data) {
            setAllServices(servicesList.data);
        }
    }, [servicesList]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) {
            dispatch(getServicesList({
                accountId: accountData?._id,
                token,
                start: 0,
                length: 100, // Aumentiamo il numero per ottenere più servizi in una volta
                draw: 1,
                order: [],
                columns: [{ data: "name" }, { data: "code" }, { data: "unitTypeLabel" }]
            }));
            dispatch(resetRefreshNow());
            setDataTableKey(Date.now()); // Aggiorniamo la chiave per forzare il re-render della tabella
        }
    }, [rightModalIsRefreshNow]);
    
    // Aggiorna la tabella quando il modal viene chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            // Aggiorna la tabella
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);
    

    


    // Funzione per aprire la modale di conferma eliminazione
    const handleDeleteService = (serviceId, serviceName) => {
        dispatch(setModal({ id: serviceId, mode: "delete" }));
    };
    
    return (
        <div className="page">
            {servicesLoading && <LoadingSection isFixed />}
            <h1><span className="font-light">Servizi / </span>Lista Servizi</h1>
            <div className="box">

                {/* Nuovo servizio */}
                <div className="flex justify-end mb-4">
                    <button className="primary" onClick={() => dispatch(setModal({ mode: 'add' }))}>
                        <DynamicIcon iconClass="fa-solid fa-plus" />Nuovo Servizio
                    </button>
                </div>

                {/* DataTable per i servizi */}
                <div className="w-full my-4 overflow-x-auto">
                    <DataTable
                        key={dataTableKey}
                        columns={[
                            { 
                                id: 'code', 
                                label: 'CODICE',
                                width: '20%',
                                render: (service) => (
                                    <div className="text-left uppercase">
                                        {service.code || '-'}
                                    </div>
                                )
                            },
                            { 
                                id: 'name', 
                                label: 'NOME',
                                width: '40%',
                                render: (service) => (
                                    <div className="text-left uppercase">
                                        {service.name || '-'}
                                    </div>
                                )
                            },
                            { 
                                id: 'unitTypeLabel', 
                                label: 'UNITA',
                                width: '20%',
                                render: (service) => (
                                    <div className="text-left uppercase">
                                        {service.unitTypeLabel || '-'}
                                    </div>
                                )
                            },
                            { 
                                id: 'actions', 
                                label: 'OPZ',
                                width: '20%',
                                render: (service) => (
                                    <div className="flex gap-3 justify-center">
                                        <button 
                                            style={{ color: 'inherit' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({ 
                                                    isOpen: true,
                                                    mode: 'edit',
                                                    id: service.id
                                                }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        <button 
                                            style={{ color: 'inherit' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteService(service.id, service.name);
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                )
                            }
                        ]}
                        data={allServices || []}
                        loading={servicesLoading}
                        totalRecords={servicesList?.recordsFiltered || allServices?.length || 0}
                        pageSize={10}
                        onPageChange={handlePageChange}
                        hasSearch={true}
                        onSearch={handleSearch}
                        initialSearchTerm={searchTerm}
                        emptyMessage="Nessun servizio trovato"
                        renderCustomRow={(service, index, isEven, tableStyles) => {
                            // Funzione per gestire il click sulla riga
                            const handleRowClick = () => {
                                dispatch(setModal({
                                    isOpen: true,
                                    mode: 'view',
                                    id: service.id
                                }));
                            };
                            
                            return (
                                <div 
                                    key={service.id}
                                    className="data-table-row cursor-pointer"
                                    style={{
                                        backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                        color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                    }}
                                    onClick={handleRowClick}
                                >
                                    {/* Colonna CODICE */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="Codice">
                                        <div className="text-left uppercase">
                                            {service.code || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna NOME */}
                                    <div className="data-table-cell" style={{ width: '40%' }} data-label="Nome">
                                        <div className="text-left uppercase">
                                            {service.name || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna UNITÀ */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="Unità">
                                        <div className="text-left uppercase">
                                            {service.unitTypeLabel || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna AZIONI */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="Azioni">
                                        <div className="flex gap-3 justify-center">
                                            <button 
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(setModal({ 
                                                        isOpen: true,
                                                        mode: 'edit',
                                                        id: service.id
                                                    }));
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-edit" />
                                            </button>
                                            <button 
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteService(service.id, service.name);
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            </div>

            {/* RIGHT MODAL per inserimento, modifica o eliminazione servizio */}
            {
                rightModalIsOpen && <RightModal>
                    {(rightModalMode === 'add' || rightModalMode === 'edit') && <AddEditService serviceId={rightModalServiceId} />}
                    {rightModalMode === 'delete' && <DeleteServiceConfirmation 
                        serviceId={rightModalServiceId} 
                        serviceName={allServices.find(service => service.id === rightModalServiceId)?.name || ""}
                    />}
                    {
                        rightModalMode === 'view' && rightModalServiceId &&
                        <div className="w-full p-8">
                            <h1>Dettagli Servizio</h1>
                            <div className="light-text mt-[-24px]">Id servizio: {rightModalServiceId}</div>
                        </div>
                    }
                </RightModal>
            }
        </div>
    );
}
